<template>
  <Grid
    gaf="column"
    gap="var(--r-space-md)"
    justify-content="start"
    align-items="center"
    class="stakeholders-column full-height"
  >
    <FormCreateStakeholder
      :showing="stakeholderModal"
      :departments="departments"
      @close="stakeholderModal = false"
      @refresh="newStakeholderCreated"
    />

    <ConfirmationDialog
      ref="confirmDialog"
      confirm-label="Yes, remove"
      confirm-btn-type="danger"
      cancel-label="no, cancel"
    />

    <RInitialsIcon
      v-for="stakeholder of assignedStakeholders"
      :key="stakeholder.id"
      :text="stakeholder.name"
      @mouseenter.native="showMenu(stakeholder.id)"
      @mouseleave.native="hideMenu($event, stakeholder.id)"
    >
      <q-menu
        :ref="`menu-${stakeholder.id}`"
        anchor="center end"
        self="bottom left"
        :offset="[-5, 0]"
        content-class="q-pa-sm shadow3 r-popup"
        @mouseleave="hideMenu($event, stakeholder.id)"
      >
        <Grid gaf="column" align-items="center" gap="var(--r-space-md)" class="text-secondary-5">
          <Grid>
            <div class="text-body-4">{{ stakeholder.name }}</div>
            <div class="text-caption-3">{{ stakeholder.email }}</div>
          </Grid>

          <RBtn type="text" size="small" @click="onDeleteStakeholder(stakeholder)">Delete</RBtn>
        </Grid>
      </q-menu>
    </RInitialsIcon>

    <div class="plus-icon">
      <PlusIcon />

      <LmTooltip content-class="shadow-3 bg-white text-caption-3">
        <div class="text-secondary-5">Add Stakeholder</div>
      </LmTooltip>

      <RPopup :items="stakeholderOptions" max-height="400px" @popup-item="onPopupItem" />
    </div>
  </Grid>
</template>

<script>
import * as apiTo from '@/modular-api'
// Libraries
import Vue from 'vue'
import { mapGetters } from 'vuex'
// Components
import RInitialsIcon from '@/ui/RInitialsIcon'
import RPopup from '@/ui/RPopup'
import PlusIcon from '@/ui/icons/PlusIcon'
import ConfirmationDialog from '@/ui/ConfirmationDialog'
import FormCreateStakeholder from '@components/forms/FormCreateStakeholder'

export default Vue.extend({
  components: {
    RInitialsIcon,
    RPopup,
    PlusIcon,
    ConfirmationDialog,
    FormCreateStakeholder
  },

  data: () => ({
    stakeholderModal: false
  }),

  computed: {
    ...mapGetters('entity-labels', ['departmentLabel']),

    assignedStakeholders() {
      const items = this.params.data.stakeholders

      return items.map(i => ({
        name: `${i.first_name} ${i.last_name}`,
        email: i.email,
        id: i.id
      }))
    },

    stakeholderOptions() {
      const availableStakeholders = this.params.context.costAllocationCmp.stakeholders

      const modifiedStakeholders = availableStakeholders.map(i => ({
        name: i.name,
        label: i.name,
        subLabel: i.departments.length
          ? `${this.departmentLabel.plural}: ${i.departments.map(d => d.name).join(',')}`
          : null,
        id: i.id,
        departmentId: this.params.data.id
      }))

      return [
        { name: 'new-stakeholder', label: 'Create new stakeholder', borderBottom: true, stickyTop: true },
        ...modifiedStakeholders
      ]
    },

    departments() {
      return this.params.context.costAllocationCmp.departments
    }
  },

  methods: {
    showMenu(stakeholderId) {
      this.$refs[`menu-${stakeholderId}`][0].show()
    },

    hideMenu(event, stakeholderId) {
      if (!event.relatedTarget.classList.contains('q-menu')) this.$refs[`menu-${stakeholderId}`][0].hide()
    },

    onPopupItem(item) {
      if (item.name === 'new-stakeholder') this.stakeholderModal = true
      else this.newStakeholderAssigned(item)
    },

    onDeleteStakeholder(stakeholder) {
      const dialog = this.$refs.confirmDialog.show({
        title: `Are you sure you want to remove \n ${stakeholder.name} as a stakeholder?`
      })

      dialog.onCancel(hide => hide())
      dialog.onConfirm(this.deleteStakeholder.bind(this, stakeholder))
    },

    deleteStakeholder(stakeholder, hideModal, setModalLoading) {
      const parent = this.params.context.costAllocationCmp
      setModalLoading()

      apiTo
        .DELETE_SUBSCRIPTION_PERIOD_STAKEHOLDER(parent.selectedPeriodId, stakeholder.id, this.params.data.id)
        .then(() => {
          hideModal()
          const newStakeholders = this.params.data.stakeholders.filter(s => s.id !== stakeholder.id)

          if (!newStakeholders.length && !this.params.data.id) {
            parent.getStakes(parent.selectedPeriodId)
          } else {
            const node = this.params.api.getRowNode(this.params.data.id)
            node.setData({ ...this.params.data, stakeholders: newStakeholders })
          }
        })
        .finally(() => setModalLoading(false))
    },

    newStakeholderCreated(stakeholder) {
      this.params.context.costAllocationCmp.stakeholders.push(stakeholder)

      this.newStakeholderAssigned({
        departmentId: this.params.data.id,
        id: stakeholder.id
      })
    },

    newStakeholderAssigned(stakeholder) {
      const parent = this.params.context.costAllocationCmp

      apiTo
        .ADD_STAKEHOLDER_TO_SUBSCRIPTION_PERIOD(
          parent.selectedPeriodId,
          stakeholder.departmentId,
          stakeholder.id,
          this.params.data.id
        )
        .then(() => parent.getStakes(parent.selectedPeriodId))
    }
  }
})
</script>

<style lang="scss" scoped>
.stakeholders-column {
  .plus-icon {
    font-size: var(--r-space-sp);
  }
}
</style>
